import React from 'react';
import PropTypes from 'prop-types';
import { getModelDisplayName } from '../../constants/models';
import * as Diff from 'diff';

const VersionComparison = ({ version1, version2, onClose }) => {
  const renderDiff = (original, refined, label) => {
    if (!original && !refined) return null;
    
    const hasChanges = original !== refined;
    
    // If no changes, show a simple message
    if (!hasChanges) {
      return (
        <div className="mb-6">
          <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
            <div className="flex items-center gap-2 text-text-secondary">
              <svg className="w-5 h-5 text-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span className="text-sm">No changes in {label}</span>
            </div>
          </div>
        </div>
      );
    }
    
    // Compute line-by-line diff
    const diffResult = Diff.diffLines(original || '', refined || '');
    
    let lineNumber1 = 1;
    let lineNumber2 = 1;
    
    return (
      <div className="mb-6">
        <div className="bg-bg-secondary border border-border-primary rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 py-2 px-3 md:px-4 bg-bg-tertiary border-b border-border-primary">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
              <span className="text-sm font-semibold text-text-primary">
                {label}
              </span>
              {version1?.commit_id && version2?.commit_id && (
                <span className="text-xs text-text-tertiary flex items-center flex-wrap gap-1">
                  <code className="px-2 py-0.5 bg-bg-primary rounded whitespace-nowrap">{version1.commit_id.substring(0, 7)}</code>
                  <span>→</span>
                  <code className="px-2 py-0.5 bg-bg-primary rounded whitespace-nowrap">{version2.commit_id.substring(0, 7)}</code>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-text-tertiary">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-500/20 border border-red-500/50 rounded-sm flex-shrink-0"></span>
                <span className="whitespace-nowrap">Removed</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-green-500/20 border border-green-500/50 rounded-sm flex-shrink-0"></span>
                <span className="whitespace-nowrap">Added</span>
              </span>
            </div>
          </div>
          
          {/* Diff content */}
          <div className="max-h-96 overflow-y-auto overflow-x-auto font-mono text-xs">
            {diffResult.map((part, index) => {
              const lines = part.value.split('\n');
              // Remove last empty line if it exists
              if (lines[lines.length - 1] === '') {
                lines.pop();
              }
              
              return lines.map((line, lineIndex) => {
                const currentLineNumber1 = !part.added ? lineNumber1++ : null;
                const currentLineNumber2 = !part.removed ? lineNumber2++ : null;
                
                let bgClass = 'bg-bg-primary';
                let borderClass = '';
                let textClass = 'text-text-primary';
                let symbol = ' ';
                
                if (part.added) {
                  bgClass = 'bg-green-500/10';
                  borderClass = 'border-l-2 border-green-500';
                  textClass = 'text-green-400';
                  symbol = '+';
                } else if (part.removed) {
                  bgClass = 'bg-red-500/10';
                  borderClass = 'border-l-2 border-red-500';
                  textClass = 'text-red-400';
                  symbol = '-';
                }
                
                return (
                  <div
                    key={`${index}-${lineIndex}`}
                    className={`flex ${bgClass} ${borderClass} hover:bg-opacity-20`}
                  >
                    {/* Line numbers - hidden on very small screens */}
                    <div className="hidden sm:flex flex-shrink-0">
                      <span className="w-8 sm:w-10 md:w-12 px-1 sm:px-2 py-1 text-right text-text-tertiary select-none bg-bg-tertiary border-r border-border-primary text-[10px] sm:text-xs">
                        {currentLineNumber1 || ''}
                      </span>
                      <span className="w-8 sm:w-10 md:w-12 px-1 sm:px-2 py-1 text-right text-text-tertiary select-none bg-bg-tertiary border-r border-border-primary text-[10px] sm:text-xs">
                        {currentLineNumber2 || ''}
                      </span>
                    </div>
                    
                    {/* Symbol column */}
                    <span className={`w-5 sm:w-6 px-0.5 sm:px-1 py-1 text-center ${textClass} font-bold select-none flex-shrink-0`}>
                      {symbol}
                    </span>
                    
                    {/* Line content */}
                    <pre className={`flex-1 px-1 sm:px-2 py-1 ${textClass} overflow-x-auto whitespace-pre text-[10px] sm:text-xs`}>
                      {line || ' '}
                    </pre>
                  </div>
                );
              });
            })}
          </div>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="h-full flex flex-col bg-bg-primary rounded-xl shadow-lg border border-border-primary overflow-hidden">
      <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3 py-4 px-4 lg:px-6 border-b border-border-primary flex-shrink-0">
        <div className="flex-1">
          <h2 className="text-lg lg:text-xl font-semibold text-text-primary">Version Comparison</h2>
          <p className="text-xs sm:text-sm text-text-secondary mt-1 flex flex-wrap items-center gap-1">
            <span>Comparing</span>
            <code className="text-xs font-mono px-2 py-0.5 bg-bg-tertiary text-text-primary rounded whitespace-nowrap">{version1?.commit_id?.substring(0, 7)}</code>
            <span>with</span>
            <code className="text-xs font-mono px-2 py-0.5 bg-bg-tertiary text-text-primary rounded whitespace-nowrap">{version2?.commit_id?.substring(0, 7)}</code>
          </p>
        </div>
        <button 
          className="p-2 text-text-tertiary hover:text-text-primary hover:bg-bg-hover rounded-md transition-colors duration-fast self-start" 
          onClick={onClose}
          aria-label="Close comparison"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 py-4 px-4 lg:px-6 bg-bg-secondary border-b border-border-primary flex-shrink-0">
          <div className="bg-bg-primary border border-border-primary rounded-lg p-3 md:p-4">
            <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-primary-50 text-primary-600 text-xs rounded">V1</span>
              <span>Version 1</span>
            </h4>
            <div className="space-y-2">
              {version1.created_by && (
                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                  <span className="text-xs text-text-tertiary font-semibold sm:font-normal sm:min-w-[60px]">By:</span>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <span className="text-xs text-text-primary">{version1.created_by.username}</span>
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                <span className="text-xs text-text-tertiary font-semibold sm:font-normal sm:min-w-[60px]">Created:</span>
                <span className="text-xs text-text-primary">{formatDate(version1.created_at)}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                <span className="text-xs text-text-tertiary font-semibold sm:font-normal sm:min-w-[60px]">Model:</span>
                <span className="text-xs text-text-primary">{getModelDisplayName(version1.model)}</span>
              </div>
              {version1.commit_message && (
                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                  <span className="text-xs text-text-tertiary font-semibold sm:font-normal sm:min-w-[60px]">Commit:</span>
                  <span className="text-xs text-text-primary break-words">{version1.commit_message}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-bg-primary border border-border-primary rounded-lg p-3 md:p-4">
            <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-accent-50 text-accent-600 text-xs rounded">V2</span>
              <span>Version 2</span>
            </h4>
            <div className="space-y-2">
              {version2.created_by && (
                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                  <span className="text-xs text-text-tertiary font-semibold sm:font-normal sm:min-w-[60px]">By:</span>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <span className="text-xs text-text-primary">{version2.created_by.username}</span>
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                <span className="text-xs text-text-tertiary font-semibold sm:font-normal sm:min-w-[60px]">Created:</span>
                <span className="text-xs text-text-primary">{formatDate(version2.created_at)}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                <span className="text-xs text-text-tertiary font-semibold sm:font-normal sm:min-w-[60px]">Model:</span>
                <span className="text-xs text-text-primary">{getModelDisplayName(version2.model)}</span>
              </div>
              {version2.commit_message && (
                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                  <span className="text-xs text-text-tertiary font-semibold sm:font-normal sm:min-w-[60px]">Commit:</span>
                  <span className="text-xs text-text-primary break-words">{version2.commit_message}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-4 px-4 lg:py-6 lg:px-6">
          {renderDiff(
            version1?.system_prompt_output,
            version2?.system_prompt_output,
            'System Prompt'
          )}

          {renderDiff(
            version1?.user_prompt_output,
            version2?.user_prompt_output,
            'User Prompt'
          )}

          {renderDiff(
            version1?.few_shot_examples,
            version2?.few_shot_examples,
            'Few-Shot Examples'
          )}
        </div>
      </div>
  );
};

VersionComparison.propTypes = {
  version1: PropTypes.object.isRequired,
  version2: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default VersionComparison;
